import {
    ConsolidationGroupResponse,
    ConsolidationRelationshipResponse,
} from "@common/schemas/domains/accounting/consolidation.schema";
import {
    ConsolidationEntityRelationshipType,
    ConsolidationGroupStatus,
    ConsolidationTreatment,
} from "@common/types/domains/accounting/consolidation";
import { isActiveRelationship } from "./ownershipGraph.helpers";

export const OWNERSHIP_ACTIVE_GROUP_MESSAGE =
    "Unconsolidate the group before changing ownership relationships.";

function isRelationshipBackingActiveGroup({
    relationship,
    groups,
}: {
    relationship: ConsolidationRelationshipResponse;
    groups: ConsolidationGroupResponse[];
}): boolean {
    if (
        relationship.type !== ConsolidationEntityRelationshipType.ownership ||
        !isActiveRelationship(relationship)
    ) {
        return false;
    }

    return groups.some(
        (group) =>
            group.status === ConsolidationGroupStatus.active &&
            group.parentEntityUuid === relationship.sourceEntityUuid &&
            group.members.some(
                (member) =>
                    member.entityUuid === relationship.targetEntityUuid &&
                    member.consolidationTreatment ===
                        ConsolidationTreatment.consolidates,
            ),
    );
}

export function isConflictingRelationshipBackingActiveGroup({
    relationships,
    groups,
}: {
    relationships: ConsolidationRelationshipResponse[];
    groups: ConsolidationGroupResponse[];
}): boolean {
    return relationships.some((relationship) =>
        isRelationshipBackingActiveGroup({ relationship, groups }),
    );
}

/**
 * Returns the block message when deleting this relationship would break an
 * active consolidation group; otherwise null (delete may proceed).
 */
export function getOwnershipRelationshipDeleteBlockMessage({
    relationship,
    groups,
}: {
    relationship: ConsolidationRelationshipResponse;
    groups: ConsolidationGroupResponse[];
}): string | null {
    if (isRelationshipBackingActiveGroup({ relationship, groups })) {
        return OWNERSHIP_ACTIVE_GROUP_MESSAGE;
    }
    return null;
}

function getActiveGroups(
    groups: ConsolidationGroupResponse[],
): ConsolidationGroupResponse[] {
    return groups.filter(
        (group) => group.status === ConsolidationGroupStatus.active,
    );
}

function isActiveConsolidationGroupParent({
    entityUuid,
    groups,
}: {
    entityUuid: string;
    groups: ConsolidationGroupResponse[];
}): boolean {
    return getActiveGroups(groups).some(
        (group) => group.parentEntityUuid === entityUuid,
    );
}

function isConsolidatingGroupMember({
    entityUuid,
    groups,
}: {
    entityUuid: string;
    groups: ConsolidationGroupResponse[];
}): boolean {
    return getActiveGroups(groups).some((group) =>
        group.members.some(
            (member) =>
                member.entityUuid === entityUuid &&
                member.consolidationTreatment ===
                    ConsolidationTreatment.consolidates,
        ),
    );
}

/**
 * Ownership creates are locked for entities inside an active consolidation
 * group, except the ultimate parent may gain a parent (become a child).
 */
export function isOwnershipCreateLockedByConsolidation({
    sourceEntityUuid,
    targetEntityUuid,
    groups,
}: {
    sourceEntityUuid: string;
    targetEntityUuid: string;
    groups: ConsolidationGroupResponse[];
}): boolean {
    if (
        isActiveConsolidationGroupParent({
            entityUuid: targetEntityUuid,
            groups,
        })
    ) {
        return false;
    }

    return (
        isActiveConsolidationGroupParent({
            entityUuid: sourceEntityUuid,
            groups,
        }) ||
        isConsolidatingGroupMember({ entityUuid: sourceEntityUuid, groups }) ||
        isConsolidatingGroupMember({ entityUuid: targetEntityUuid, groups })
    );
}
